import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getPolicyQuote } from "service/policy/getPolicyQuote";
import { sendSmsMessage } from "service/notificationservice/sendSmsMessage";
import UpdatePaymentFrequency from "service/paymentServices/UpdatePaymentFrequency";
import { getName, stringFormattingToCurrency } from "js/formatting";
import { getAllOwnersFromParties } from "js/utilities";
import { getAutopayFrequencyDate } from "js/utilities/getAutopayFrequencyDate";
import Toast from "components/General/Toast";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import PaymentDialog from "components/Profile/Dialogs/PaymentDialog";
import config from "config/config";

export default function EditPaymentFrequency() {
  let navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const { csrUser } = useSelector((s) => s.csrUser);
  const { policies } = useSelector((state) => state.data);
  const { policy_id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState();
  const [frequency, setFrequency] = useState([]);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const [csrUserState, setCsrUserState] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [loggedUser, setLoggedUser] = useState("");
  const [ccQuotes, setCCQuotes] = useState([]);
  const [consentPayload, setConsentPayload] = useState();
  const [billingMode, setBillingMode] = useState();
  const { register, handleSubmit, getValues, setValue } = useForm({
    defaultValues: {
      billingmode: "",
    },
  });
  const [toastDetail, setToastDetail] = useState({
    type: undefined,
    title: "",
    message: "",
  });

  useBreadcrumb([
    { name: t("CustomerEditPaymentFrequency-home"), url: "/policies" },
    { name: t("CustomerEditPaymentFrequency-payments"), url: "/payment" },
    { name: t("CustomerEditPaymentFrequency-payment-frequency"), url: "#" },
  ]);

  const errorOccurred = (title, message) => {
    setToastDetail({
      type: "error",
      title: title,
      message: message,
    });
  };

  const checkedContinueIsDisabled = (billingMode) => {
    if (billingMode) {
      if (billingMode === selectedPolicy?.billingMode?.toLowerCase())
        setIsContinueDisabled(true);
      else setIsContinueDisabled(false);
    } else {
      setIsContinueDisabled(false);
    }
  };

  const getBillingModeCode = (billingMode) => {
    switch (billingMode) {
      case "quarterly":
        return "04";
      case "semi-annual":
        return "02";
      case "annual":
        return "01";
    }
  };

  const updatePaymentFrequency = (data) => {
    setToastDetail({
      type: undefined,
      title: "",
      message: "",
    });
    const requestData = {
      REPLACE_POLICY_ID: selectedPolicy?.contractID,
      REPLACE_POLICY_OWNER_NAME: getName(loggedUser?.person),
      REPLACE_VIEW_BALANCE_PAID: "hidden",
      REPLACE_VIEW_AMOUNT: "show",
      REPLACE_POLICY_AMOUNT: stringFormattingToCurrency(
        ccQuotes.find(
          (creditCardquote) =>
            creditCardquote?.billingMode?.toLowerCase() === data?.billingmode
        )?.totalModalPremiumAmount
      ),
      REPLACE_POLICY_DATE: getAutopayFrequencyDate(
        selectedPolicy,
        selectedPolicy?.billingMode?.toLowerCase()
      ),
      REPLACE_POLICY_CARD_DETAILS: "",
      REPLACE_VIEW_FREQUENCY: "show",
      REPLACE_POLICY_FREQUENCY: data?.billingmode,
    };
    setConsentPayload(requestData);
    setOpenPaymentDialog(true);
  };

  useEffect(() => {
    if (policies?.length > 0) {
      const policy = policies?.find((p) => p.contractID === policy_id);
      if (policy) {
        setSelectedPolicy(policy);
        setBillingMode(policy?.billingMode?.toLowerCase());
        setValue("billingmode", policy?.billingMode?.toLowerCase());
      }
      const owners = getAllOwnersFromParties(policy?.parties);
      const loggedUser = owners?.find(
        (x) => x?.person?.taxId === user?.identityNumber
      );
      setLoggedUser(loggedUser);
    }
  }, [policies, policy_id]);

  const UpdateFrequency = (consentNameEntered) => {
    setOpenPaymentDialog(false);
    if (csrUser?.userId) {
      //csr user can not save autopay
      setCsrUserState(true);
    } else {
      setIsLoading(true);
      const payload = {
        policyId: selectedPolicy?.contractID,
        oldPolicyId: selectedPolicy?.oldPolicy,
        billingMode: getValues("billingmode"),
        userid: user?.userId,
        billingModeCode: getBillingModeCode(getValues("billingmode")),
        policyOwnerName: getName(loggedUser?.person),
        consentNameEntered: consentNameEntered,
      };
      //api call for update autopay
      UpdatePaymentFrequency(payload)
        .then(() => {
          setIsLoading(false);
          setIsContinueDisabled(true);
          setToastDetail({
            type: "success",
            title: t("AuthCustomerEditPaymentFrequency-warningTitle"),
            message: t("AuthCustomerEditPaymentFrequency-warningMessage", {
              existingBillingMode: billingMode,
              updatedBillingMode: getValues("billingmode"),
            }),
          });
          //Send SMS Message
          if (config.COMPANY_NAME !== "everlake")
            sendSmsMessage(t("SMS-body"), config.SMS_TO_PHONE_NUMBER);
        })
        .catch((error) => {
          setIsLoading(false);
          console.error(
            "Server refused to connect, please try again later.",
            error
          );
          errorOccurred(
            t("AuthCustomerEditPaymentFrequency-updateAPIErrorTitle"),
            t("AuthCustomerEditPaymentFrequency-updateAPIErrorMessage")
          );
        });
    }
  };

  useEffect(() => {
    function getPaymentProfile() {
      setIsLoading(true);
      if (selectedPolicy?.contractID) {
        // get policy quote for annual, semi-annual and quarterly
        getPolicyQuote(selectedPolicy?.contractID)
          .then((response) => {
            const creditCardquotes = response.filter(
              (quoteDetail) => quoteDetail?.billingType?.toLowerCase() === "eft"
            );
            if (creditCardquotes) {
              setCCQuotes(creditCardquotes);
              setFrequency([
                {
                  title: `Quarterly ($${
                    creditCardquotes.find(
                      (creditCardquote) =>
                        creditCardquote?.billingMode?.toLowerCase() ===
                        "quarterly"
                    ).totalModalPremiumAmount
                  })`,
                  value: "quarterly",
                },
                {
                  title: `Semi-Annually ($${
                    creditCardquotes.find(
                      (creditCardquote) =>
                        creditCardquote?.billingMode?.toLowerCase() ===
                        "semi-annual"
                    ).totalModalPremiumAmount
                  })`,
                  value: "semi-annual",
                },
                {
                  title: `Annually ($${
                    creditCardquotes.find(
                      (creditCardquote) =>
                        creditCardquote?.billingMode?.toLowerCase() === "annual"
                    ).totalModalPremiumAmount
                  })`,
                  value: "annual",
                },
              ]);
              setIsLoading(false);
            } else {
              console.error("getPolicyQuote returns.", response);
              setIsLoading(false);
            }
          })
          .catch((error) => {
            console.error(
              "Server refused to connect, please try again later.",
              error
            );
            errorOccurred(
              t("AuthCustomerEditPaymentFrequency-getAPIErrorTitle"),
              t("AuthCustomerEditPaymentFrequency-getAPIErrorMessage")
            );
            setIsLoading(false);
          });
      }
    }
    getPaymentProfile();
  }, [selectedPolicy]);

  return (
    <>
      <EverlakeLoadingOverlay isLoading={isLoading}>
        {toastDetail?.type && (
          <Toast
            title={toastDetail.title}
            message={toastDetail.message}
            type={toastDetail.type}
          />
        )}
        <div
          className={`container mb-8 ${toastDetail?.type ? "mt-6" : "mt-0"}`}
        >
          <h1>{t("AuthCustomerEditPaymentFrequency-title")}</h1>
          <hr />
        </div>
        <div className="container ui-component-spacing mt-8">
          <div className="w-full">
            <form onSubmit={handleSubmit(updatePaymentFrequency)}>
              <div className="ui-card relative mb-4 md:mb-6">
                <div className="mt-2 px-6 py-6">
                  <div>
                    <fieldset className="flex flex-col gap-1">
                      {frequency.map((option) => (
                        <div key={option.title} className="pb-2">
                          <label
                            className={`inline-flex items-center text-sm  cursor-pointer ${
                              selectedPolicy?.billingMode.toLowerCase() !==
                              option.value
                                ? "font-normal"
                                : undefined
                            }`}
                          >
                            <input
                              type="radio"
                              className="mr-2 cursor-pointer"
                              value={option.value}
                              {...register("billingmode")}
                              onChange={(e) => {
                                checkedContinueIsDisabled(e.target.value);
                              }}
                            />
                            {option.title}
                          </label>
                        </div>
                      ))}
                    </fieldset>
                    <h3 className="text-md font-bold pt-4">
                      <strong>
                        {t("AuthCustomerEditPaymentFrequency-disclaimer-1")}
                      </strong>
                    </h3>
                  </div>
                  <hr className="col-span-12" />
                  <h3 className="text-md">
                    {t("AuthCustomerEditPaymentFrequency-disclaimer-2")}
                  </h3>
                </div>
              </div>
              <div className="text-right">
                <button
                  type="button"
                  className="ui-btn-secondary"
                  aria-label={t(
                    "AuthCustomerEditPaymentFrequency-button-goBack-ariaLabel"
                  )}
                  onClick={() => navigate("/payment")}
                >
                  {t("AuthCustomerEditPaymentFrequency-button-goBack")}
                </button>
                <button
                  className="ui-btn-primary"
                  aria-label={t(
                    "AuthCustomerEditPaymentFrequency-button-continue-ariaLabel"
                  )}
                  disabled={isContinueDisabled}
                >
                  {t("AuthCustomerEditPaymentFrequency-button-continue")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </EverlakeLoadingOverlay>

      <GeneralPurposeDialog
        title={t("CSRErrorMessage-title")}
        desc={t("CSRErrorMessage-message")}
        state={csrUserState}
        setState={setCsrUserState}
        showCloseButton={false}
      />

      {consentPayload && (
        <PaymentDialog
          showPaymentDialog={openPaymentDialog}
          setShowPaymentDialog={setOpenPaymentDialog}
          data={consentPayload}
          action={(inputValue) => UpdateFrequency(inputValue)}
        />
      )}
    </>
  );
}
